import { prop, type Signal } from '@tempots/dom'
import { actions as actionsService } from '@/services/gateway'
import { isApiError, isInsufficientCreditsError } from '@/services/errors'
import { Workspace } from '@/state/workspace'
import type {
  ActionDetail,
  ActionExecuteResponse,
  CostBreakdown,
} from '@/services/types'

export interface ExecuteError {
  success: false
  error: string
  errorType?: 'insufficient_credits' | 'generic'
  availableCredits?: number
  requiredCredits?: number
}

export interface ActionDetailState {
  action: Signal<ActionDetail | null>
  costEstimate: Signal<CostBreakdown | null>
  calculating: Signal<boolean>
  executing: Signal<boolean>
  executeResult: Signal<
    (ActionExecuteResponse & { success: true }) | ExecuteError | null
  >
}

export interface ActionDetailActions {
  loadData: () => Promise<ActionDetail>
  calculateCost: (parameters: unknown) => Promise<void>
  executeAction: (parameters: unknown) => Promise<void>
}

/**
 * Action Detail State Manager
 *
 * This is NOT a React hook - it's a factory function that creates state and actions
 * for the action detail page. It uses:
 * - prop() for reactive state
 * - Async operations for data fetching (returned promises)
 * - Direct service calls for mutations
 *
 * In Tempo, we don't need hooks - just create signals and async functions.
 *
 * @example
 * ```typescript
 * const { action, loadData, executeAction } = useActionDetail(actionName)
 *
 * // Use with Async component:
 * Async(loadData(), {
 *   pending: () => LoadingState(),
 *   then: () => html.div(action.map(a => a.name))
 * })
 * ```
 */
export function useActionDetail(
  actionName: Signal<string>
): ActionDetailState & ActionDetailActions {
  // State
  const action = prop<ActionDetail | null>(null)
  const costEstimate = prop<CostBreakdown | null>(null)
  const calculating = prop(false)
  const executing = prop(false)
  const executeResult = prop<
    (ActionExecuteResponse & { success: true }) | ExecuteError | null
  >(null)

  // Load action - returns the promise for use with Async component
  const loadData = async () => {
    const name = actionName.get()
    if (!name) {
      throw new Error('Action name is required')
    }

    const actionResult = await actionsService.get(name)
    action.set(actionResult)
    return actionResult
  }

  // Calculate cost based on form parameters
  // This is a mutation - could be wrapped in Mutation component if needed
  const calculateCost = async (parameters: unknown) => {
    const name = actionName.get()
    if (!name) {
      costEstimate.set(null)
      return
    }

    calculating.set(true)

    try {
      const result = await actionsService.calculateCost(name, { parameters })
      costEstimate.set(result.breakdown)
    } catch {
      costEstimate.set(null)
    } finally {
      calculating.set(false)
    }
  }

  // Execute action
  // This is a mutation - could be wrapped in Mutation component if needed
  const executeAction = async (parameters: unknown) => {
    const name = actionName.get()
    if (!name) return

    executing.set(true)
    executeResult.set(null)

    try {
      console.log(parameters)
      const result = await actionsService.execute(name, parameters)

      if (result.status === 'completed') {
        executeResult.set({ ...result, success: true })
        // Refresh credits after successful execution
        Workspace.setOrganizationCredits(BigInt(result.creditsCharged * 100))
      } else {
        executeResult.set({
          success: false,
          error: result.error || 'Execution failed',
        })
      }
    } catch (err) {
      // Check for insufficient credits error (402)
      if (isApiError(err) && isInsufficientCreditsError(err)) {
        executeResult.set({
          success: false,
          error: 'Insufficient credits to execute this action',
          errorType: 'insufficient_credits',
          availableCredits: err.availableCredits,
          requiredCredits: err.requiredCredits,
        })
      } else {
        executeResult.set({
          success: false,
          error: err instanceof Error ? err.message : 'Execution failed',
          errorType: 'generic',
        })
      }
    } finally {
      executing.set(false)
    }
  }

  return {
    action,
    costEstimate,
    calculating,
    executing,
    executeResult,
    loadData,
    calculateCost,
    executeAction,
  }
}
